"use client"

import * as React from "react"
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react"
import { ArrowLeft, ArrowRight } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: "horizontal" | "vertical"
  setApi?: (api: CarouselApi) => void
  dir?: "ltr" | "rtl"
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  scrollTo: (index: number) => void
  canScrollPrev: boolean
  canScrollNext: boolean
  selectedIndex: number
  scrollSnaps: number[]
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />")
  }

  return context
}

function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  dir = "rtl",
  ...props
}: React.ComponentProps<"div"> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
      direction: dir,
    },
    plugins
  )
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([])

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
    setSelectedIndex(api.selectedScrollSnap())
  }, [])

  const onInit = React.useCallback((api: CarouselApi) => {
    if (!api) return
    setScrollSnaps(api.scrollSnapList())
  }, [])

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev()
  }, [api])

  const scrollNext = React.useCallback(() => {
    api?.scrollNext()
  }, [api])

  const scrollTo = React.useCallback(
    (index: number) => {
      api?.scrollTo(index)
    },
    [api]
  )

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // For RTL, arrow keys should be reversed
      if (dir === "rtl") {
        if (event.key === "ArrowLeft") {
          event.preventDefault()
          scrollNext()
        } else if (event.key === "ArrowRight") {
          event.preventDefault()
          scrollPrev()
        }
      } else {
        if (event.key === "ArrowLeft") {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === "ArrowRight") {
          event.preventDefault()
          scrollNext()
        }
      }
    },
    [scrollPrev, scrollNext, dir]
  )

  React.useEffect(() => {
    if (!api || !setApi) return
    setApi(api)
  }, [api, setApi])

  React.useEffect(() => {
    if (!api) return
    onInit(api)
    onSelect(api)
    api.on("reInit", onInit)
    api.on("reInit", onSelect)
    api.on("select", onSelect)

    return () => {
      api?.off("select", onSelect)
      api?.off("reInit", onInit)
      api?.off("reInit", onSelect)
    }
  }, [api, onSelect, onInit])

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation:
          orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        scrollTo,
        canScrollPrev,
        canScrollNext,
        selectedIndex,
        scrollSnaps,
        dir,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn("relative", className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        dir={dir}
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div
      ref={carouselRef}
      className="overflow-hidden"
      data-slot="carousel-content"
    >
      <div
        className={cn(
          "flex",
          orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
          className
        )}
        {...props}
      />
    </div>
  )
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation } = useCarousel()

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className
      )}
      {...props}
    />
  )
}

function CarouselPrevious({
  className,
  variant = "outline",
  size = "icon",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev, canScrollPrev, dir } = useCarousel()
  const isRtl = dir === "rtl"

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={cn(
        "absolute size-8 rounded-full",
        orientation === "horizontal"
          ? isRtl
            ? "top-1/2 -right-12 -translate-y-1/2"
            : "top-1/2 -left-12 -translate-y-1/2"
          : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      {isRtl ? <ArrowRight /> : <ArrowLeft />}
      <span className="sr-only">Previous slide</span>
    </Button>
  )
}

function CarouselNext({
  className,
  variant = "outline",
  size = "icon",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext, canScrollNext, dir } = useCarousel()
  const isRtl = dir === "rtl"

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={cn(
        "absolute size-8 rounded-full",
        orientation === "horizontal"
          ? isRtl
            ? "top-1/2 -left-12 -translate-y-1/2"
            : "top-1/2 -right-12 -translate-y-1/2"
          : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      {isRtl ? <ArrowLeft /> : <ArrowRight />}
      <span className="sr-only">Next slide</span>
    </Button>
  )
}

// Dots pagination component
interface CarouselDotsProps extends React.ComponentProps<"div"> {
  activeClassName?: string
  inactiveClassName?: string
}

function CarouselDots({
  className,
  activeClassName = "!w-[22px] !bg-primary-500",
  inactiveClassName = "bg-[#D9D9D9]",
  ...props
}: CarouselDotsProps) {
  const { scrollTo, selectedIndex, scrollSnaps } = useCarousel()

  if (scrollSnaps.length <= 1) return null

  return (
    <div
      className={cn("flex justify-center gap-1.5", className)}
      data-slot="carousel-dots"
      {...props}
    >
      {scrollSnaps.map((_, index) => (
        <button
          key={index}
          type="button"
          className={cn(
            "size-2 rounded-full transition-all duration-300 cursor-pointer",
            index === selectedIndex ? activeClassName : inactiveClassName
          )}
          onClick={() => scrollTo(index)}
          aria-label={`Go to slide ${index + 1}`}
          aria-current={index === selectedIndex ? "true" : undefined}
        />
      ))}
    </div>
  )
}

// Progress counter (e.g., "1/5")
interface CarouselCounterProps extends React.ComponentProps<"div"> {
  format?: (current: number, total: number) => string
}

function CarouselCounter({
  className,
  format = (current, total) => `${current}/${total}`,
  ...props
}: CarouselCounterProps) {
  const { selectedIndex, scrollSnaps } = useCarousel()

  if (scrollSnaps.length <= 1) return null

  return (
    <div
      className={cn(
        "absolute text-white bottom-5 left-4 z-10 px-1 py-0.5 rounded-[5px] bg-black-500/50 text-sm font-medium",
        className
      )}
      data-slot="carousel-counter"
      {...props}
    >
      {format(selectedIndex + 1, scrollSnaps.length)}
    </div>
  )
}

// Progress bars (for stories)
interface CarouselProgressBarsProps extends React.ComponentProps<"div"> {
  progress?: number
  barClassName?: string
  activeBarClassName?: string
}

function CarouselProgressBars({
  className,
  progress = 0,
  barClassName = "bg-white/30",
  activeBarClassName = "bg-white",
  ...props
}: CarouselProgressBarsProps) {
  const { selectedIndex, scrollSnaps } = useCarousel()

  if (scrollSnaps.length <= 1) return null

  return (
    <div
      className={cn("flex gap-1 w-full px-4 py-3", className)}
      data-slot="carousel-progress-bars"
      {...props}
    >
      {scrollSnaps.map((_, index) => {
        let barProgress = 0
        if (index < selectedIndex) {
          barProgress = 100
        } else if (index === selectedIndex) {
          barProgress = progress
        }

        return (
          <div
            key={index}
            className={cn("flex-1 h-0.5 rounded-full overflow-hidden", barClassName)}
          >
            <div
              className={cn("h-full rounded-full transition-all duration-75 ease-linear", activeBarClassName)}
              style={{ width: `${barProgress}%` }}
            />
          </div>
        )
      })}
    </div>
  )
}

export {
  type CarouselApi,
  type CarouselOptions,
  type CarouselPlugin,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselDots,
  CarouselCounter,
  CarouselProgressBars,
  useCarousel,
}
